/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import {
    PARTNER_BRANDING_LOGO_HEIGHT_1080P,
    PARTNER_BRANDING_LOGO_WIDTH_1080P,
} from '../../config';
import { DeviceFeatures } from '../../core/device';
import {
    EmptyResponseError,
    MsoDefaultBrandingError,
    PartialResponseError,
    SlsGlobalEndpointError,
} from '../../core/error';
import { DefaultServicesClientImpl } from '../../core/impls/DefaultServiceClientImpl';
import { MsoDefaultBrandingUiBundleService } from '../../core/infrastructure/MsoDefaultBrandingUiBundleService';
import { SlsServiceGlobalEndpointsService } from '../../core/infrastructure/SlsServiceGlobalEndpointsService';
import { AppStartOperationsLogger, VuLogger } from '../../core/logging';
import { setPartnerBrandingImageUrlAction } from '../appl/appl.actions';
import {
    hideErrorOverlay,
    showTemporaryServiceConnectionIssueOverlay,
} from '../error/erroroverlays.reducers';
import { showSplashScreen } from '../screens/screens.reducers';

const TAG = 'presignin';
export const prepareForAppLaunch = () => async (dispatch: Function) => {
    Diagnostics.info(TAG, 'Inside prepareForAppLaunch');
    const defaultServices = new DefaultServicesClientImpl();

    try {
        Diagnostics.info(TAG, 'Fetch SlsGlobalEndpoints');
        AppStartOperationsLogger.logStartSlsGlobalEndpointsGet();

        await defaultServices.fetchSlsGlobalEndpoints();

        AppStartOperationsLogger.logSlsGlobalEndpointsSuccessful();
    } catch (error: any) {
        let serviceError = error;
        if (!(error instanceof PartialResponseError || error instanceof EmptyResponseError)) {
            const slsError: any = error?.response?.data;
            serviceError = new SlsGlobalEndpointError(slsError?.code, slsError?.message);
        }
        dispatch(showTemporaryServiceConnectionIssueOverlay(serviceError));
        Diagnostics.error(TAG, 'Failed to fetch SlsGlobalEndpoints', serviceError);
        AppStartOperationsLogger.logSlsGlobalEndpointsFailed(serviceError);
        Diagnostics.info(TAG, 'Wait for SlsGlobalEndpoints to recover');
        VuLogger.logLoginFailureEvent(serviceError);
        AppStartOperationsLogger.logUIErrorCode(serviceError.getCode());

        await defaultServices.waitForSlsGlobalEndpoints();
        dispatch(hideErrorOverlay());

        AppStartOperationsLogger.logSlsGlobalEndpointsRestoration();
    }

    try {
        MsoDefaultBrandingUiBundleService.initialize(
            SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.MSO_DEFAULT_BRANDING_SERVICE,
            ),
            DeviceFeatures.isWhinyLoggingEnabled(),
        );
        Diagnostics.info(TAG, 'Fetch MsoDefaultBrandingUiInfo');
        AppStartOperationsLogger.logMsoDefaultBrandingUiBundleGet();
        await defaultServices.fetchMsoDefaultBrandingUiInfo();

        AppStartOperationsLogger.logMsoDefaultBrandingUiBundleSuccessful();
    } catch (error: any) {
        const brandingServiceError = error?.response?.data;
        const brandingUiBundleError = new MsoDefaultBrandingError(
            brandingServiceError?.message,
            brandingServiceError?.code,
        );
        Diagnostics.error(TAG, 'Failed to fetch MsoDefaultBrandingUiInfo', brandingServiceError);
        AppStartOperationsLogger.logMsoDefaultBrandingUiBundleFailed(brandingUiBundleError);
        AppStartOperationsLogger.logUIErrorCode(brandingUiBundleError.getCode());
    } finally {
        dispatch(showSplashScreen());
        const partnerBrandingImageUrl1080p = defaultServices.getPartnerBrandingImageUrl(
            PARTNER_BRANDING_LOGO_WIDTH_1080P,
            PARTNER_BRANDING_LOGO_HEIGHT_1080P,
        );
        dispatch(setPartnerBrandingImageUrlAction(partnerBrandingImageUrl1080p));
    }
};
